import {
  Resource, ResourcePermissions, PermissionsTypes, PERMISSIONS_ORDER,
  FilteredResource,
  FilteredResourcePermission,
} from 'model/role-permissions';
import * as Yup from 'yup';
import { checkFunctionPermissionRaw, checkFunctionPermissionsBatch } from './api-client';
import { loadFilteredResourcePermission, loadPermission } from './api-serializer';
import { dayJsDate } from './date';

export function isPermissionGreater(perm1: PermissionsTypes, perm2: PermissionsTypes): boolean {
  return PERMISSIONS_ORDER.indexOf(perm1) > PERMISSIONS_ORDER.indexOf(perm2);
}

export function getHighestPermission(perms: PermissionsTypes[]): PermissionsTypes {
  if (!perms || perms?.length === 0) {
    return PermissionsTypes.Deny;
  }
  return perms.sort((a, b) => (isPermissionGreater(a, b) ? -1 : 1))[0];
}

export function isPermissionSetGreater(perms1: PermissionsTypes[], perms2: PermissionsTypes[]): boolean {
  const highestPerm1 = getHighestPermission(perms1);
  const highestPerm2 = getHighestPermission(perms2);
  return isPermissionGreater(highestPerm1, highestPerm2);
}

export function userHasPermissionOnResource(
  permission: PermissionsTypes,
  resource: string,
  permissions: ResourcePermissions | undefined,
  defaultPosition = false,
): boolean {
  // Fail closed by default — unknown or unregistered resources are denied.
  // The backend is authoritative, but failing open on the frontend leaks UI to unauthorised users.
  if (!permissions || !permissions[resource]?.resource) return defaultPosition;

  let highestUserPermission = getHighestPermission(permissions[resource]?.resource);
  // FilterFunction grants filtered read-level access. Treat it as Read for gate
  // checks — the backend does the same elevation in has_permission_for_operation.
  if (highestUserPermission === PermissionsTypes.FilterFunction) {
    highestUserPermission = PermissionsTypes.Read;
  }
  // Is the highest user permission greater or equal to the required permission
  return !isPermissionGreater(permission, highestUserPermission);
}

export function userHasPermissionOnResourceId(
  permission: PermissionsTypes,
  resource: string,
  objectId?: string,
  filteredResources?: FilteredResource,
  defaultPosition = false,
): boolean {
  if (!filteredResources || !filteredResources[resource] || !objectId) return defaultPosition;
  const userPermission = filteredResources[resource].find((entry) => entry.id === objectId)?.permission;

  if (!userPermission) return defaultPosition;

  return !isPermissionGreater(permission, userPermission);
}

export function userHasPermissionOnProperty(
  permission: PermissionsTypes,
  resource: string,
  property: string,
  permissions: ResourcePermissions | undefined,
  defaultPosition: boolean = true,
): boolean {
  if (!permissions) {
    return defaultPosition;
  }
  if (permissions[resource]?.properties[property]?.length > 0) {
    const highestPermission = getHighestPermission(permissions[resource]?.properties[property]);
    return !isPermissionGreater(permission, highestPermission);
  }
  return userHasPermissionOnResource(permission, resource, permissions);
}

export function makeRequiredIfPermitted(permissions: ResourcePermissions | undefined, resource: string, obj: { [key: string]: Yup.Schema }): { [key: string]: Yup.Schema } {
  Object.keys(obj).forEach((key) => {
    if (!userHasPermissionOnProperty(PermissionsTypes.Read, resource, key, permissions)) {
      obj[key] = obj[key].optional();
    }
  });
  return obj;
}

export async function loadPermittedData(
  permissions: ResourcePermissions | undefined,
  data: { [key: string]: [() => Promise<any>, React.Dispatch<any>] },
  minPermission?: PermissionsTypes,
) {
  const effectivePermission = minPermission ?? PermissionsTypes.Read;
  return Promise.all(
    Object.keys(data)
      .filter((resource) => userHasPermissionOnResource(effectivePermission, resource, permissions))
      .map((r) => data[r][0]().then((result) => data[r][1](result))),
  );
}

// True when the caller can use the audit-logged override flow on the
// rewrite-history modal — i.e. has `rewrite_history_with_reason`
// ReadWrite. Forms gate the red "Override and save" button on this.
// Users with `rewrite_history` ReadWrite never see the modal at all
// (silent bypass via `isRewritingHistory` above), so this only
// matters for users who DON'T have that.
export function canOverrideRewriteHistoryWithReason(permissions?: ResourcePermissions): boolean {
  if (!permissions) return false;
  return userHasPermissionOnResource(PermissionsTypes.ReadWrite, 'rewrite_history_with_reason', permissions);
}

// True when the target date falls inside an already-invoiced period —
// i.e. target <= last_invoice_date. last_invoice_date is the end of
// the most-recent invoice (maintained by aws/lambda_api/code/invoices/
// generator.py); editing data on or before it would desync the books
// from issued invoices. Mirrors aws/lambda_api/code/permissions/
// permissions.py::user_can_rewrite_history.
export function isRewritingHistory(
  date: Date | string | null | undefined,
  lastInvoiceDate: Date | string | null | undefined,
  permissions?: ResourcePermissions,
): boolean {
  if (!date) return false;

  if (permissions && userHasPermissionOnResource(PermissionsTypes.ReadWrite, 'rewrite_history', permissions)) {
    return false;
  }

  const parsedLastInvoice = lastInvoiceDate ? (typeof lastInvoiceDate === 'string' ? dayJsDate(lastInvoiceDate) : lastInvoiceDate) : undefined;
  const parsedTargetDate = date ? (typeof date === 'string' ? dayJsDate(date) : date) : undefined;
  if (
    parsedLastInvoice instanceof Date
    && !Number.isNaN(parsedLastInvoice.getTime())
    && parsedTargetDate instanceof Date
    && !Number.isNaN(parsedTargetDate.getTime())
  ) {
    return parsedTargetDate <= parsedLastInvoice;
  }

  // No last_invoice_date — project hasn't been invoiced yet, nothing to desync.
  return false;
}

export function rewriteHistoryWarning(
  project_start_date: Date | string | null | undefined,
  date: Date | string | null | undefined,
  firstInvoiceDate: Date | string | null | undefined,
  lastInvoiceDate: Date | string | null | undefined,
  permissions?: ResourcePermissions,
): boolean {
  // If no date provided, do not warn
  if (!date) return false;

  // Parse all dates
  const targetDate = typeof date === 'string' ? dayJsDate(date) : date;
  const parsedProjectStart = project_start_date ? (typeof project_start_date === 'string' ? dayJsDate(project_start_date) : project_start_date) : undefined;
  const parsedFirstInvoice = firstInvoiceDate ? (typeof firstInvoiceDate === 'string' ? dayJsDate(firstInvoiceDate) : firstInvoiceDate) : undefined;
  const parsedLastInvoice = lastInvoiceDate ? (typeof lastInvoiceDate === 'string' ? dayJsDate(lastInvoiceDate) : lastInvoiceDate) : undefined;
  // Reject invalid dates rather than silently treating NaN comparisons as
  // false (which used to slip past the warning entirely). Mirrors the
  // guard at the top of isRewritingHistory.
  if (!(targetDate instanceof Date) || Number.isNaN(targetDate.getTime())) return false;
  // If there is a firstInvoiceDate, warn if event is between max(project_start_date, firstInvoiceDate, lastInvoiceDate)
  if (parsedFirstInvoice && parsedLastInvoice && parsedProjectStart) {
    const minDate = dayJsDate(Math.max(parsedProjectStart.getTime(), parsedFirstInvoice.getTime()));
    // Return true if targetDate is between minDate and parsedLastInvoice
    if (targetDate >= minDate && targetDate <= parsedLastInvoice) {
      return true;
    }

    if (targetDate < minDate && permissions && userHasPermissionOnResource(PermissionsTypes.ReadWrite, 'rewrite_history', permissions)) {
      return true;
    }

    return false;
  }

  // If there is no firstInvoiceDate, warn if event is before lastInvoiceDate
  if (!parsedFirstInvoice && parsedLastInvoice) {
    if (targetDate < parsedLastInvoice) {
      return true;
    }

    return false;
  }

  // If no relevant dates, do not warn

  return false;
}

const namesOf = (plural?: string[], single?: string): string[] => {
  if (plural && plural.length) return plural.filter(Boolean);
  return single ? [single] : [];
};

const resourceFunctionNames = (resource: Resource) => namesOf(resource.resource_functions, resource.resource_function);
const filterFunctionNames = (resource: Resource) => namesOf(resource.filter_resource_functions, resource.filter_resource_function);
const propertyFunctionNames = (resource: Resource, property: string) => namesOf(
  resource.properties_functions?.[property],
  resource.properties_function?.[property],
);

/*
 * Resolve every permission function a user's permissions reference — in ONE
 * request (falling back to one request per function on older backends).
 * Each function succeeds or fails on its own: a failure is logged and that
 * function simply grants nothing, instead of the old Promise.all rejecting
 * and taking ALL permissions (and the sidebar) down with it.
 */
const fetchFunctionResults = async (names: string[]): Promise<Map<string, unknown>> => {
  const unique = Array.from(new Set(names.filter(Boolean)));
  const out = new Map<string, unknown>();
  if (unique.length === 0) return out;
  try {
    const batch = await checkFunctionPermissionsBatch(unique);
    unique.forEach((name) => {
      const entry = batch?.[name];
      if (entry && entry.status === 200) out.set(name, entry.result);
      else console.error(`Permission function ${name} could not be resolved (${entry?.status ?? 'missing'})`);
    });
    return out;
  } catch (e) {
    console.error('Batch permission check unavailable; resolving functions one by one', e);
  }
  const settled = await Promise.allSettled(unique.map((name) => checkFunctionPermissionRaw(name)));
  settled.forEach((result, i) => {
    if (result.status === 'fulfilled') out.set(unique[i], result.value);
    else console.error(`Permission function ${unique[i]} could not be resolved`, result.reason);
  });
  return out;
};

const toPermission = (raw: unknown): PermissionsTypes | undefined => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== 'string') return undefined;
  try {
    return loadPermission(value);
  } catch {
    return undefined;
  }
};

const toFiltered = (raw: unknown): FilteredResourcePermission[] => {
  try {
    return loadFilteredResourcePermission(raw);
  } catch {
    return [];
  }
};

/*
 * Turn merged permissions into what the app uses:
 *  - ownPermissions: Function grants replaced by their resolved level (the
 *    highest any of the role functions gives — permissions are additive;
 *    nothing resolved → Deny, fail closed);
 *  - filteredResources: per resource, the per-record grants from ALL its
 *    filter functions, each record at the highest level any function gives.
 */
/*
 * Whose "own profile" checks apply: the borrowed prod user's person while
 * impersonating one in prod1, else the signed-in user's. Only for read-side
 * "is this mine?" checks. Anything saved with the user's person_id (settings,
 * bookmarks) keeps using `person_id`.
 */
export const effectivePersonId = (user?: { person_id?: string; impersonatedPersonId?: string } | null): string => (
  user?.impersonatedPersonId || user?.person_id || ''
);

export const resolveFunctionPermissions = async (permissions: ResourcePermissions): Promise<{
  ownPermissions: ResourcePermissions;
  filteredResources: Record<string, FilteredResourcePermission[]>;
}> => {
  const ownPermissions: ResourcePermissions = {};
  Object.entries(permissions || {}).forEach(([name, resource]) => {
    ownPermissions[name] = { ...resource, resource: [...(resource.resource || [])], properties: { ...(resource.properties || {}) } };
  });

  const needed: string[] = [];
  Object.values(ownPermissions).forEach((resource) => {
    if (resource.resource.includes(PermissionsTypes.Function)) needed.push(...resourceFunctionNames(resource));
    Object.entries(resource.properties).forEach(([prop, levels]) => {
      if (levels.includes(PermissionsTypes.Function)) needed.push(...propertyFunctionNames(resource, prop));
    });
    needed.push(...filterFunctionNames(resource));
  });
  const results = await fetchFunctionResults(needed);

  const resolveLevel = (names: string[]) => {
    const levels = names.map((n) => toPermission(results.get(n))).filter((p): p is PermissionsTypes => !!p);
    return levels.length ? getHighestPermission(levels) : PermissionsTypes.Deny;
  };

  Object.values(ownPermissions).forEach((resource) => {
    if (resource.resource.includes(PermissionsTypes.Function)) {
      resource.resource = [resolveLevel(resourceFunctionNames(resource))];
    }
  });
  Object.values(ownPermissions).forEach((resource) => {
    Object.entries(resource.properties).forEach(([prop, levels]) => {
      if (!levels.includes(PermissionsTypes.Function)) return;
      const names = propertyFunctionNames(resource, prop);
      if (names.length && !resource.resource.includes(PermissionsTypes.Deny)) {
        resource.properties[prop] = [resolveLevel(names)];
      } else if (resource.resource.length > 0 && !resource.resource.includes(PermissionsTypes.Function)) {
        resource.properties[prop] = [...resource.resource]; // Inherit resolved resource permission
      } else {
        console.error(`No function found for property ${prop}, and resource has unresolved permissions.`);
      }
    });
  });

  const filteredResources: Record<string, FilteredResourcePermission[]> = {};
  Object.entries(ownPermissions).forEach(([name, resource]) => {
    const names = filterFunctionNames(resource);
    if (names.length === 0) return;
    const byId = new Map<string, PermissionsTypes>();
    names.forEach((fn) => toFiltered(results.get(fn)).forEach((entry) => {
      const current = byId.get(entry.id);
      byId.set(entry.id, current ? getHighestPermission([current, entry.permission]) : entry.permission);
    }));
    filteredResources[name] = Array.from(byId.entries()).map(([id, permission]) => ({ id, permission }));
  });

  return { ownPermissions, filteredResources };
};
